// The hidden protein block on a chat meal plan. Issue #1789.
//
// ── WHY A HIDDEN BLOCK AND NOT A NUMBER IN THE PROSE ────────────────────────
//
// Kasandra, 2026-10-07: "estimate at plan generation, but only show if she
// wants you to." Two facts make a hidden block the only shape that satisfies
// both halves:
//
//   1. `parseMealPlanDays` is a PROSE parser. It builds `{ name }` out of free
//      text and there is no structured channel anywhere in the meal plan save
//      path, so an estimate cannot travel as a field. It has to be in the
//      message.
//   2. `run-scheduled-tasks/mealTypes.ts:98` tells the generator to "Sound
//      like a real mom planned this, not a nutritionist." Grams inline in the
//      plan text run directly against that, every time, for every user —
//      including the ones who never asked to see a number.
//
// So Kin writes the grams in a machine-readable block that is stripped before
// display and before `raw_markdown` is stored, and a per-user switch decides
// whether the number is ever shown. The estimate always exists; the display is
// opt-in. Precedent for the shape is `[SCHEDULE_JSON]` in the same prompt.
//
// ⚠️ Do not "simplify" this to grams in the prose with the switch gating the
// prompt instead. That was considered and rejected: it would put numbers in the
// visible plan whenever the switch is on, and would leave every plan saved
// while it was off permanently gramless.

import type { ParsedMealDay } from "@/lib/mealPlanParser";

export const PROTEIN_JSON_OPEN = "[PROTEIN_JSON]";
export const PROTEIN_JSON_CLOSE = "[/PROTEIN_JSON]";

/**
 * The paired block, and the orphan tags.
 *
 * ⚠️ Two patterns, not one, and the second is not paranoia. A streamed response
 * cut off mid-block leaves an opener with no closer, and a regex that only
 * matches the pair leaves `[PROTEIN_JSON]` sitting in the chat bubble as
 * literal text. `stripTodoListTag` carries the same pair for the same reason.
 */
const BLOCK_RE = /\[PROTEIN_JSON\][\s\S]*?\[\/PROTEIN_JSON\]/gi;
const ORPHAN_RE = /\[\/?PROTEIN_JSON\]/gi;

/** One meal's estimate, as Kin writes it in the block. */
export interface PlanProteinEntry {
  day: string;
  meal: string;
  protein_g: number;
}

/**
 * Collapse a label to something two spellings of the same meal agree on.
 *
 * The prose name has already been through `cleanMealLine` (which strips `**`
 * and leading bullets); the block's name comes straight from the model and can
 * differ in case, spacing, a trailing period, or a leading emoji. Everything
 * normalised away here is a difference that does not change which meal it is.
 */
function norm(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/\*\*/g, "")
    // Leading emoji, bullets, numbering, quotes.
    .replace(/^[^\p{L}\p{N}]+/u, "")
    // Trailing punctuation and whitespace.
    .replace(/[^\p{L}\p{N}]+$/u, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** A finite, non-negative, sane gram count. Anything else is not an estimate. */
function validGrams(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 500;
}

/**
 * Read the block out of a Kin response.
 *
 * Returns null when there is no block at all — distinct from `[]`, which means
 * a block was present and contained nothing usable. The caller does not
 * currently treat them differently, but "absent" and "empty" are different
 * facts about the model's output and collapsing them here would make a prompt
 * regression indistinguishable from a parse failure.
 *
 * ⚠️ Never throws. A malformed block yields null and every meal keeps a null
 * estimate, which renders as an EMPTY field rather than a zero (#1781's
 * `EntryRow`). The failure mode of this whole file is "no number", never "a
 * wrong number", and that is the property worth protecting.
 */
export function parsePlanProteinBlock(content: string): PlanProteinEntry[] | null {
  if (!content) return null;
  const match = content.match(/\[PROTEIN_JSON\]([\s\S]*?)\[\/PROTEIN_JSON\]/i);
  if (!match) return null;

  let raw: unknown;
  try {
    raw = JSON.parse(match[1].trim());
  } catch {
    return null;
  }
  if (!Array.isArray(raw)) return null;

  const out: PlanProteinEntry[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    const day = typeof rec.day === "string" ? rec.day : "";
    const meal = typeof rec.meal === "string" ? rec.meal : "";
    // An entry missing either half cannot be matched to anything, so it is
    // dropped rather than kept as a half-record that silently never applies.
    if (!norm(day) || !norm(meal)) continue;
    if (!validGrams(rec.protein_g)) continue;
    out.push({ day, meal, protein_g: rec.protein_g });
  }
  return out;
}

/**
 * Remove the block so it never reaches a human.
 *
 * ⚠️ THREE passes, and the middle one is the whole point (Copilot, PR #1794).
 * The first version removed complete blocks and then stray TAGS — which leaves
 * the JSON BODY of an unterminated block sitting in the bubble, because its
 * opener was deleted and nothing marked the rest as machine text.
 *
 * That is not an edge case. It happens on EVERY streamed response, in the
 * window between the opening tag arriving and the closing tag arriving: the
 * raw array is briefly visible. And a stream cut off mid-block leaves it
 * visible for good.
 *
 * So an unmatched opener takes everything after it to the end of the content.
 * `suppressUnclosedTodoList` in ChatMessages does exactly this for
 * [TODO_LIST], for exactly this reason.
 */
export function stripPlanProteinBlock(content: string): string {
  if (!content) return content;
  // 1 — complete blocks. After this, any opener still present is unmatched.
  let out = content.replace(BLOCK_RE, "");
  // 2 — an unmatched opener, and the partial JSON trailing it.
  const open = out.search(/\[PROTEIN_JSON\]/i);
  if (open !== -1) out = out.slice(0, open);
  // 3 — a stray closer with no opener left to pair with.
  return out.replace(ORPHAN_RE, "");
}

/**
 * Attach each estimate to the meal it belongs to.
 *
 * ⚠️ MATCHED BY DAY + NAME, NEVER BY POSITION, and this is the single most
 * important decision in the feature.
 *
 * A positional zip (nth entry → nth meal) looks simpler and is wrong in a way
 * nothing downstream can detect. `parseMealPlanDays` can legitimately produce a
 * different number of meals than the block has entries: it merges a
 * continuation bullet into the day line, drops a line that does not match its
 * day pattern, and stops entirely at the grocery boundary. Any of those shifts
 * the alignment by one and lands a real, plausible number on the wrong meal —
 * 38g of chicken filed against Tuesday's pasta. She would have no way to see
 * it, because every number present would look like a number that belongs.
 *
 * Name matching fails the other way: a name that does not line up gets NO
 * estimate, which shows as an empty field she can fill in. An absence is
 * visible; a wrong number is not.
 *
 * Each entry is consumed at most once, so a week with "Tacos" twice gives its
 * two estimates to the two taco nights in order rather than putting the first
 * one on both.
 */
export function attachPlanProteinGrams(
  days: ParsedMealDay[],
  entries: PlanProteinEntry[] | null,
): ParsedMealDay[] {
  if (!Array.isArray(days) || !entries || entries.length === 0) return days;

  // Day → queue of [normalised meal name, grams], consumed in block order.
  const byDay = new Map<string, { meal: string; protein_g: number }[]>();
  for (const e of entries) {
    const key = norm(e.day);
    const list = byDay.get(key) ?? [];
    list.push({ meal: norm(e.meal), protein_g: e.protein_g });
    byDay.set(key, list);
  }

  return days.map((day) => {
    const queue = byDay.get(norm(day?.day ?? ""));
    if (!queue) return day;
    return {
      ...day,
      meals: (day.meals ?? []).map((meal) => {
        const want = norm(meal?.name ?? "");
        const idx = queue.findIndex((q) => q.meal === want);
        if (idx === -1) return meal;
        // Splice rather than mark: consumed entries must not match a second
        // meal with the same name.
        const [hit] = queue.splice(idx, 1);
        return { ...meal, protein_g: hit.protein_g };
      }),
    };
  });
}

/**
 * The meal line on a saved meal plan card. #1789.
 *
 * `showGrams` is her `plan_grams_enabled` setting, and when it is false this
 * renders exactly what the card rendered before this issue — so the OFF state
 * is not a new code path that could drift, it is the original one.
 *
 * ⚠️ A meal with no estimate shows its name ALONE, never "(0g)". The two are
 * different claims: one says "nobody worked this out", the other says "this
 * meal has no protein in it". Plans saved before #1789 have no estimates at
 * all, so the no-estimate case is the common one for a while, not an edge.
 */
export function formatPlanMealLine(
  meals: { name?: string; protein_g?: number | null }[] | null | undefined,
  showGrams: boolean,
): string {
  if (!Array.isArray(meals)) return "";
  return meals
    .map((m) => {
      const name = (m?.name ?? "").trim();
      if (!name) return "";
      if (!showGrams) return name;
      const g = m?.protein_g;
      return typeof g === "number" && Number.isFinite(g) ? `${name} (${g}g)` : name;
    })
    .filter(Boolean)
    .join(", ");
}
